import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthGuardSkeleton } from '@/modules/shared/components/ui/AppSkeleton';
import { ApprovalLinkStatusModal } from '@/modules/shared/components/ApprovalLinkStatusModal';
import { appRoutes } from '@/modules/shared/constants/routes';
import { programRoutes } from '@/modules/shared/constants/routes';
import { useAuth } from '@/modules/shared/contexts/AuthContext';

function buildApprovalTarget(programId: string, patientId: string): string {
  return programRoutes.hcpPatientApprovalReview(programId, { patientId });
}

interface ApprovalOutcomeModal {
  title: string;
  message: string;
  actionLabel: string;
  redirectTo: string;
}

function buildHcpOverviewRoute(programId: string): string {
  return `/programs/${programId}/hcp-dashboard/overview`;
}

function buildModalState(
  errorCode: string | undefined,
  approvalStatus: string | undefined,
  patientStatus: string | undefined,
  fallbackMessage: string,
  isAuthenticated: boolean,
  programId: string,
): ApprovalOutcomeModal {
  const authenticatedTarget = isAuthenticated ? buildHcpOverviewRoute(programId) : appRoutes.login();
  const normalizedCode = (errorCode || '').trim().toUpperCase();
  const normalizedApprovalStatus = (approvalStatus || '').trim().toUpperCase();
  const normalizedPatientStatus = (patientStatus || '').trim().toUpperCase();

  if (normalizedCode === 'APPROVAL_ALREADY_PROCESSED') {
    if (
      ['APPROVED', 'ENROLLED'].includes(normalizedApprovalStatus) ||
      ['APPROVED', 'ENROLLED', 'ACTIVE'].includes(normalizedPatientStatus)
    ) {
      return {
        title: 'Patient Already Approved',
        message: 'This patient registration has already been reviewed and approved. The patient is currently enrolled in the program.',
        actionLabel: 'OK',
        redirectTo: authenticatedTarget,
      };
    }

    if (normalizedApprovalStatus === 'REJECTED' || normalizedPatientStatus === 'REJECTED') {
      return {
        title: 'Patient Registration Already Rejected',
        message: 'This patient registration has already been reviewed and rejected. No further approval action is required.',
        actionLabel: 'OK',
        redirectTo: authenticatedTarget,
      };
    }
  }

  if (normalizedCode === 'EXPIRED') {
    return {
      title: 'Approval Link Expired',
      message: 'This approval link has expired. Please contact the program team to resend the approval email.',
      actionLabel: 'Go to Login',
      redirectTo: appRoutes.login(),
    };
  }

  if (normalizedCode === 'INVALID') {
    return {
      title: 'Invalid Approval Link',
      message: 'This approval link is invalid or no longer available.',
      actionLabel: 'Go to Login',
      redirectTo: appRoutes.login(),
    };
  }

  return {
    title: 'Invalid Approval Link',
    message: fallbackMessage || 'This approval link is invalid or no longer available.',
    actionLabel: 'Go to Login',
    redirectTo: appRoutes.login(),
  };
}

export default function HcpApprovalAutoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hcpApprovalLogin, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [modalState, setModalState] = useState<ApprovalOutcomeModal | null>(null);
  const attemptedRef = useRef(false);

  const programId = searchParams.get('programId')?.trim() || '';
  const patientId = searchParams.get('patientId')?.trim() || '';
  const approvalToken = searchParams.get('approvalToken')?.trim() || '';

  const validationError = useMemo(() => {
    if (!programId || !patientId || !approvalToken) {
      return 'Approval link is incomplete. Please use the latest approval email link.';
    }

    if (!/^[0-9a-fA-F-]{36}$/.test(programId) || !/^[0-9a-fA-F-]{36}$/.test(patientId)) {
      return 'Approval link contains invalid program or patient identifiers.';
    }

    return null;
  }, [approvalToken, patientId, programId]);

  useEffect(() => {
    if (attemptedRef.current) {
      return;
    }

    if (validationError) {
      attemptedRef.current = true;
      setModalState(buildModalState(undefined, undefined, undefined, validationError, isAuthenticated, programId));
      setIsLoading(false);
      return;
    }

    attemptedRef.current = true;

    void (async () => {
      const result = await hcpApprovalLogin(programId, patientId, approvalToken);
      if (result.success) {
        sessionStorage.setItem(`epprem_approval_token:${programId}:${patientId}`, approvalToken);
        navigate(buildApprovalTarget(programId, patientId), { replace: true });
        return;
      }

      setModalState(
        buildModalState(
          result.errorCode,
          result.approvalStatus,
          result.patientStatus,
          result.message || 'Approval token login failed.',
          isAuthenticated,
          programId,
        ),
      );
      setIsLoading(false);
    })();
  }, [approvalToken, hcpApprovalLogin, isAuthenticated, navigate, patientId, programId, validationError]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background px-6 py-6">
        <AuthGuardSkeleton message="Signing you in from approval link..." className="mx-auto max-w-[720px]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <ApprovalLinkStatusModal
        open={!!modalState}
        onAction={() => {
          const target = modalState?.redirectTo ?? appRoutes.login();
          navigate(target, { replace: true });
        }}
        title={modalState?.title ?? 'Approval Link'}
        message={modalState?.message ?? ''}
        actionLabel={modalState?.actionLabel ?? 'OK'}
      />
    </div>
  );
}
